import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api } from "../lib/api";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AutoGlowBrand } from "./components/AutoGlowLogo";
import BackButton from "./components/BackButton";
import LangSwitcher from "./components/LangSwitcher";
import ThemeSwitch from "./components/ThemeSwitch";
import BottomNav from "../components/layout/BottomNav";
import GestorContextBanner from "../components/GestorContextBanner";
import { ChangelogButton } from "../components/ChangelogButton";
import { PushNotificationsToggle } from "../components/PushNotificationsToggle";
import NotificationInboxDropdown from "../components/notifications/NotificationInboxDropdown";
import OrgSwitcher from "../components/OrgSwitcher";
import { getLocale, setLocale as persistLocale, t } from "../lib/i18n";
import {
  ChartBar, Wrench, Receipt, CalendarBlank, UsersThree,
  Coins, Gear, SignOut, House, Tag, Scales, Bell, CreditCard, Sparkle, AddressBook, Ticket, Buildings,
  ChatCircleDots, Repeat, Tray, ChartLineUp,
  List as ListIcon, X,
} from "@phosphor-icons/react";

// Las labels se traducen en runtime con t(labelKey, locale) en el render.
const adminNav = [
  { to: "/", labelKey: "nav.dashboard", icon: ChartBar, end: true },
  { to: "/services", labelKey: "nav.servicios", icon: Wrench },
  { to: "/customers", labelKey: "nav.clientes", icon: AddressBook },
  { to: "/catalog", labelKey: "nav.catalogo", icon: Tag },
  { to: "/providers", labelKey: "nav.proveedores", icon: Buildings },
  { to: "/expenses", labelKey: "nav.gastos", icon: Receipt },
  { to: "/calendar", labelKey: "nav.agenda", icon: CalendarBlank },
  { to: "/chats", labelKey: "nav.mensajes", icon: ChatCircleDots },
  { to: "/leads", labelKey: "nav.leads", icon: Tray },
  { to: "/analytics", labelKey: "nav.analiticas", icon: ChartLineUp },
  { to: "/availability", labelKey: "nav.avisos", icon: Bell },
  { to: "/team", labelKey: "nav.equipo", icon: UsersThree },
  { to: "/payroll", labelKey: "nav.liquidaciones", icon: Coins },
  { to: "/vouchers", labelKey: "nav.bonos", icon: Ticket },
  { to: "/tax", labelKey: "nav.aeat", icon: Scales },
  { to: "/billing", labelKey: "nav.facturacion", icon: CreditCard },
  { to: "/recurring-invoices", labelKey: "nav.contratos", icon: Repeat },
  { to: "/settings", labelKey: "nav.ajustes", icon: Gear },
];

const colabNav = [
  { to: "/", labelKey: "nav.dashboard", icon: House, end: true },
  { to: "/services", labelKey: "nav.servicios", icon: Wrench },
  { to: "/customers", labelKey: "nav.clientes", icon: AddressBook },
  { to: "/expenses", labelKey: "nav.gastos", icon: Receipt },
  { to: "/calendar", labelKey: "nav.agenda", icon: CalendarBlank },
  { to: "/chats", labelKey: "nav.mensajes", icon: ChatCircleDots },
  { to: "/availability", labelKey: "nav.midisponibilidad", icon: Bell },
  { to: "/my-invoice", labelKey: "nav.mifactura", icon: Coins },
  { to: "/my-settings", labelKey: "nav.ajustes", icon: Gear },
];

// NEW · Empleado / Operario: sin acceso financiero.
// Fase 2A · P0-1 · rutas dedicadas /empleado/* para mantener aislado del AppShell financiero.
const empleadoNav = [
  { to: "/", labelKey: "nav.dashboard", icon: House, end: true },
  { to: "/empleado/agenda", labelKey: "nav.agenda", icon: CalendarBlank },
  { to: "/empleado/clientes", labelKey: "nav.clientes", icon: AddressBook },
  { to: "/chats", labelKey: "nav.mensajes", icon: ChatCircleDots },
  { to: "/empleado/mi-perfil", labelKey: "nav.ajustes", icon: Gear },
];

const gestorNav = [
  { to: "/", labelKey: "nav.dashboard", icon: ChartBar, end: true },
  { to: "/expenses", labelKey: "nav.gastos", icon: Receipt },
  { to: "/providers", labelKey: "nav.proveedores", icon: Buildings },
  { to: "/tax", labelKey: "nav.aeat", icon: Scales },
  { to: "/my-settings", labelKey: "nav.ajustes", icon: Gear },
];

const ROLE_LABEL = { admin: "Titular", colaborador: "Colaborador", gestor: "Gestor", empleado: "Empleado" };

export default function AppShell() {
  const { user, org, logout } = useAuth();
  const navigate = useNavigate();
  const nav = user?.role === "admin" ? adminNav
            : user?.role === "gestor" ? gestorNav
            : user?.role === "empleado" ? empleadoNav
            : colabNav;
  const [unread, setUnread] = useState(0);
  const [leadsUnread, setLeadsUnread] = useState(0);
  const [billing, setBilling] = useState(null);
  const prevLeadsUnreadRef = useRef(null);  // null = primer tick, evita toast inicial

  useEffect(() => {
    if (user?.role !== "admin") return;
    let stop = false;

    // Carga seen lead IDs de sessionStorage para no notificar duplicados
    const seenKey = "ag:leads:seen";
    const loadSeen = () => {
      try { return new Set(JSON.parse(sessionStorage.getItem(seenKey) || "[]")); }
      catch { return new Set(); }
    };
    const saveSeen = (set) => {
      try { sessionStorage.setItem(seenKey, JSON.stringify(Array.from(set).slice(-100))); }
      catch { /* ignore */ }
    };

    const tick = async () => {
      try {
        const [{ data: stats }, { data: bill }, leadsRes] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/billing/status"),
          api.get("/admin/leads/summary").catch(() => null),
        ]);
        if (stop) return;
        setUnread(stats.unread_availability_count || 0);
        setBilling(bill);
        const newUnread = leadsRes?.data?.unread || 0;
        setLeadsUnread(newUnread);

        // Toast: solo cuando NO es el primer tick y unread ha incrementado
        const prev = prevLeadsUnreadRef.current;
        if (prev !== null && newUnread > prev) {
          try {
            const { data: latest } = await api.get("/admin/leads?status=new&page=1&page_size=5&sort=created_desc");
            const seen = loadSeen();
            const fresh = (latest.items || []).filter((l) => !seen.has(l.id)).slice(0, 3);
            fresh.forEach((lead) => {
              const desc = lead.service
                ? `${lead.name} ha solicitado información sobre ${lead.service}`
                : `${lead.name} te ha enviado una consulta`;
              toast.success("Nuevo lead recibido", {
                description: desc,
                action: {
                  label: "Ver lead",
                  onClick: () => navigate(`/leads`),
                },
                duration: 8000,
              });
              seen.add(lead.id);
            });
            saveSeen(seen);
          } catch { /* ignore */ }
        } else if (prev === null) {
          // Primer tick: marcar leads existentes como vistos para no spamear
          try {
            const { data: latest } = await api.get("/admin/leads?status=new&page=1&page_size=20&sort=created_desc");
            const seen = loadSeen();
            (latest.items || []).forEach((l) => seen.add(l.id));
            saveSeen(seen);
          } catch { /* ignore */ }
        }
        prevLeadsUnreadRef.current = newUnread;
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [user?.role, navigate]);

  const handleLogout = () => { logout(); navigate("/login", { replace: true }); };
  const [locale, setLocaleState] = useState(getLocale());
  const setLocale = (code) => { persistLocale(code); setLocaleState(code); };
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  // Cerrar sidebar al navegar en móvil
  useEffect(() => { setSidebarOpen(false); }, [pathname]);

  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)] flex">
      {/* Overlay sólo en móvil cuando sidebar abierto */}
      {sidebarOpen && (
        <button type="button" aria-label="Cerrar menú"
          onClick={()=>setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          data-testid="sidebar-overlay"
        />
      )}
      {/* Sidebar — drawer en móvil, fijo en lg+ */}
      <aside className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:static top-0 bottom-0 left-0 z-50 w-64 bg-[var(--ag-midnight-soft)] border-r border-[var(--ag-border)] flex flex-col transition-transform duration-200`}
             data-testid="app-sidebar"
             aria-hidden={!sidebarOpen ? undefined : "false"}>
        <div className="p-5 border-b border-[var(--ag-border)]">
          <div className="flex items-center justify-between gap-2">
            <Link to="/welcome" className="shrink-0" data-testid="sidebar-logo" aria-label="Volver al inicio AutoGlow">
              <AutoGlowBrand size={22} />
            </Link>
            <button type="button" onClick={()=>setSidebarOpen(false)} className="lg:hidden text-[var(--ag-text-dim)] hover:text-white p-1.5 rounded" data-testid="sidebar-close-btn" aria-label="Cerrar menú">
              <X size={18} />
            </button>
          </div>
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Centro</div>
            <div className="text-sm font-medium truncate" data-testid="org-name">{org?.name || "—"}</div>
            <div className="text-xs text-[var(--ag-text-dim)] tabular-nums">NIF {org?.nif || "—"}</div>
          </div>
          {user?.role === "admin" && billing && (
            <a href="/billing" className="mt-3 flex items-center justify-between gap-2 px-2 py-1.5 bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded text-[10px] hover:border-[var(--ag-gold)]/40 transition-colors" data-testid="sidebar-billing-status">
              <span className="uppercase tracking-wider text-[var(--ag-text-dim)]">Plan</span>
              {billing.subscription_status === "active" && <span className="text-emerald-400 font-bold">ACTIVO</span>}
              {billing.subscription_status === "trialing" && <span className="text-[var(--ag-gold)] font-bold flex items-center gap-1"><Sparkle size={9} weight="fill" /> TRIAL</span>}
              {billing.subscription_status === "trial_expired" && <span className="text-red-400 font-bold">EXPIRADO</span>}
              {billing.subscription_status === "past_due" && <span className="text-red-400 font-bold">PAGO PEND.</span>}
            </a>
          )}
        </div>
        <nav className="flex-1 px-2 py-4 space-y-0.5 overflow-y-auto">
          {nav.map(({ to, label, labelKey, icon: Icon, end }) => {
            const lbl = labelKey ? t(labelKey, locale) : (label || "");
            return (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm rounded transition-colors ${
                  isActive ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold" : "text-[var(--ag-text-dim)] hover:bg-[var(--ag-midnight)] hover:text-white"
                }`
              }
              data-testid={`nav-${(labelKey || label || to).toLowerCase().replace(/\s+/g, "-").replace(/[&./]/g, "")}`}
            >
              <Icon size={16} weight="duotone" />
              <span className="flex-1">{lbl}</span>
              {to === "/availability" && user?.role === "admin" && unread > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="nav-availability-badge">{unread}</span>
              )}
              {to === "/leads" && user?.role === "admin" && leadsUnread > 0 && (
                <span className="bg-[var(--ag-gold)] text-[var(--ag-midnight)] text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full min-w-[18px] text-center" data-testid="nav-leads-badge">{leadsUnread}</span>
              )}
            </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[var(--ag-border)] p-4">
          <div className="text-xs text-[var(--ag-text-dim)]">Sesión iniciada</div>
          <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</div>
          <Badge className="bg-[var(--ag-aqua)]/20 text-[var(--ag-aqua-soft)] hover:bg-[var(--ag-aqua)]/20 rounded text-[10px] uppercase tracking-wider mt-1 mb-3 border border-[var(--ag-aqua)]/30">{ROLE_LABEL[user?.role] || user?.role}</Badge>
          <Button onClick={handleLogout} variant="outline" size="sm" className="w-full bg-transparent border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:bg-[var(--ag-midnight)] hover:text-white" data-testid="logout-btn">
            <SignOut size={14} className="mr-1.5" /> Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto">
        {user?.role === "admin" && billing && billing.subscription_status === "trial_expired" && (
          <div className="bg-red-900/40 border-b border-red-700 px-6 py-3 text-sm text-red-200 flex items-center justify-between gap-4 flex-wrap">
            <span>⚠ Tu prueba gratuita ha expirado. Activa la suscripción para seguir usando AutoGlow.</span>
            <a href="/billing" className="ag-btn-gold px-4 py-1.5 text-xs">Pagar ahora</a>
          </div>
        )}
        <div className="border-b border-[var(--ag-border)] bg-[var(--ag-midnight)]/50 px-4 md:px-8 py-2.5 flex items-center justify-between gap-3" data-testid="app-topbar">
          <div className="flex items-center gap-2">
            <button type="button" onClick={()=>setSidebarOpen(true)}
              className="lg:hidden text-[var(--ag-text-dim)] hover:text-white p-1.5 rounded -ml-1.5"
              aria-label="Abrir menú" data-testid="sidebar-open-btn">
              <ListIcon size={20} weight="bold" />
            </button>
            <BackButton testId="app-back-btn" label={t("atras", locale)} />
          </div>
          <div className="flex items-center gap-2">
            <OrgSwitcher />
            <ChangelogButton className="hidden sm:inline-flex" />
            <NotificationInboxDropdown />
            <PushNotificationsToggle role="user" className="hidden sm:inline-flex border-[var(--ag-border)] bg-transparent text-[var(--ag-text-dim)] hover:text-white hover:bg-[var(--ag-midnight)]" label="" />
            <ThemeSwitch />
            <LangSwitcher locale={locale} onChange={setLocale} dark />
          </div>
        </div>
        <div className="p-4 md:p-8 max-w-[1400px] pb-24 lg:pb-8">
          {/* Fase 2D · Banner de contexto para gestor multi-org (visible siempre que role=gestor) */}
          {user?.role === "gestor" && <GestorContextBanner />}
          <Outlet />
        </div>
        {/* Fase 2A · P0-4 · Mobile BottomNav (hidden on desktop) */}
        <BottomNav role={user?.role} />
      </main>
    </div>
  );
}
